import { decompressFromUTF16 } from 'lz-string'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import eventBus from '@/eventBus'
import { dietsService } from '@/services/dietsService'
import { useDietsStore } from '@/store/dietsStore'
import { useGlobalStore } from '@/store/globalStore'
import { getDietDaysCount } from '@/utils/dietDays'
import { pushDataLayer } from '@/utils/pushDataLayer'
import { reportError } from '@/utils/reportError'

import type { DietType } from '@/store/types'
import type { StoreDiet } from '@/types/Diet'

export const useDietDetailActions = (props: { dietId: number; dietType: DietType }) => {
  const route = useRoute()
  const dietStore = useDietsStore()
  const globalStore = useGlobalStore()
  const { hasPerm, showAppUpgradePlanModal } = globalStore
  const { user } = storeToRefs(globalStore)
  const {
    copiedDietCalories,
    copiedDietStatus,
    diet,
    redoHistory,
    repeatedDishesAvailable,
    repeatedDishesAndIngredientsActive,
    undoHistory,
  } = storeToRefs(dietStore)
  const {
    copyDiet,
    deleteCopiedDiet,
    deleteDiet,
    pasteDiet,
    redo,
    toggleRepeatedDishesAndIngredientsActive,
    undo,
  } = dietStore

  const assignToLeaderModal = ref(false)
  const copyToPatientModal = ref(false)
  const pasteDietModal = ref(false)
  const printDietModal = ref(false)
  const shareDietModal = ref(false)
  const deleteDietModal = ref(false)
  const copyingDiet = ref(false)
  const copiedDaysCount = ref(0)
  const copiedMealsCount = ref(0)
  const patientId = computed(() =>
    diet.value && 'dietitian_patient' in diet.value ? diet.value.dietitian_patient.id : undefined,
  )
  const dietDaysCount = computed(() => getDietDaysCount(diet.value))
  const currentMealsCount = computed(() => diet.value?.data.meals.length ?? 0)
  const currentCalories = computed(() => diet.value?.data.norms.calories ?? 0)
  const undoRedoEnabled = computed(
    () =>
      ['DietDetailWeek', 'DietDetailDay'].includes(route.name?.toString() ?? '') &&
      props.dietType !== 'system',
  )
  const canAssignDietToLeader = computed(
    () => hasPerm('accounts.diet_assign_to_leader') && ['user', 'patient'].includes(props.dietType),
  )
  const canCopyDiet = computed(
    () =>
      (['system', 'nfz'].includes(props.dietType) || !copiedDietStatus.value) && !copyingDiet.value,
  )
  const canPasteDiet = computed(
    () => ['user', 'patient', 'university'].includes(props.dietType) && copiedDietStatus.value,
  )
  const isPasteDietDisabled = computed(() => Boolean(diet.value?.data.start_date))
  const canShareDiet = computed(() => ['user', 'patient', 'university'].includes(props.dietType))
  const canDeleteDiet = computed(
    () => ['user', 'patient'].includes(props.dietType) && route.name === 'DietDetailWeek',
  )
  const track = (event: string) => {
    pushDataLayer({ event })
  }

  const handleUndo = () => {
    if (!hasPerm('accounts.diet_undo_redo')) {
      showAppUpgradePlanModal()
      return
    }
    undo()
    eventBus.emit('undoOrRedo')
  }
  const handleRedo = () => {
    if (!hasPerm('accounts.diet_undo_redo')) {
      showAppUpgradePlanModal()
      return
    }
    redo()
    eventBus.emit('undoOrRedo')
  }
  const handleRepeatedDishes = () => {
    if (!repeatedDishesAvailable.value) return
    if (!hasPerm('accounts.diet_show_repeated_dish')) {
      showAppUpgradePlanModal()
      return
    }
    toggleRepeatedDishesAndIngredientsActive()
  }
  const reportCopiedSystemDiet = async () => {
    try {
      await dietsService.saveCopiedSystemDietInFile([
        {
          name: diet.value?.data.name ?? 'jadłospis',
          type: 'system-diets',
          user: user?.value?.email ?? '',
        },
      ])
    } catch (error) {
      reportError(error, 'Error while saving copied system diet in file')
    }
  }
  const handleCopyDiet = async () => {
    track('diet_meal_plan_copied')
    copyingDiet.value = true
    setTimeout(() => {
      copyDiet()
      copyingDiet.value = false
    }, 10)
    if (props.dietType === 'system') await reportCopiedSystemDiet()
  }
  const openPasteModal = () => {
    copiedDaysCount.value = 0
    copiedMealsCount.value = 0
    pasteDietModal.value = true
    try {
      const copiedDiet = JSON.parse(
        decompressFromUTF16(localStorage.getItem('copiedDiet') ?? ''),
      ) as StoreDiet | null
      if (!copiedDiet) return
      copiedDaysCount.value = getDietDaysCount(copiedDiet)
      copiedMealsCount.value = copiedDiet.data.meals.length
    } catch (error) {
      reportError(error, 'Error while parsing copied diet from storage')
    }
  }
  const cancelPaste = () => {
    track('diet_meal_plan_paste_modal_cancelled')
    pasteDietModal.value = false
  }
  const confirmPaste = (calories: number) => {
    track('diet_meal_plan_paste_modal_pasted')
    pasteDiet(calories)
    pasteDietModal.value = false
  }
  const clearPasteStorage = () => {
    track('diet_meal_plan_paste_modal_cleared')
    deleteCopiedDiet()
    pasteDietModal.value = false
  }
  const openPrintModal = () => {
    printDietModal.value = true
    track('diet_print_click')
  }
  const openShareModal = () => {
    track('diet_share_click')
    if (!hasPerm('accounts.diet_share')) {
      showAppUpgradePlanModal()
      return
    }
    shareDietModal.value = true
  }
  const confirmDelete = () => {
    deleteDiet()
    deleteDietModal.value = false
  }

  return {
    assignToLeaderModal,
    canAssignDietToLeader,
    canCopyDiet,
    cancelPaste,
    canDeleteDiet,
    canPasteDiet,
    canShareDiet,
    clearPasteStorage,
    confirmDelete,
    confirmPaste,
    copiedDaysCount,
    copiedDietCalories,
    copiedMealsCount,
    copyingDiet,
    copyToPatientModal,
    currentCalories,
    currentMealsCount,
    deleteDietModal,
    dietDaysCount,
    handleCopyDiet,
    handleRedo,
    handleRepeatedDishes,
    handleUndo,
    isPasteDietDisabled,
    openPasteModal,
    openPrintModal,
    openShareModal,
    pasteDietModal,
    patientId,
    printDietModal,
    redoHistory,
    repeatedDishesAvailable,
    repeatedDishesAndIngredientsActive,
    shareDietModal,
    track,
    undoHistory,
    undoRedoEnabled,
  }
}
